import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Options pour les profiles (COMME DANS TutorialCreate)
const PROFILE_OPTIONS = [
  { value: '2', label: 'Contrôleur', icon: 'bi-person-check', color: '#1976d2' },
  { value: '3', label: 'Gestionnaire', icon: 'bi-person-gear', color: '#388e3c' },
  { value: '4', label: 'Agent', icon: 'bi-person-video', color: '#f57c00' }
];

// Options pour les statuts (COMME DANS TutorialCreate)
const STATUT_OPTIONS = [
  { value: 'brouillon', label: 'Brouillon' },
  { value: 'publie', label: 'Publié' },
  { value: 'archive', label: 'Archivé' }
];

const TutorialEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [currentProfile, setCurrentProfile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoSource, setVideoSource] = useState('url');
  
  // États du formulaire - MODIFIÉ pour utiliser profile_video
  const [form, setForm] = useState({
    titre_video: "",
    description_video: "",
    url_video: "",
    profile_video: "2", // CHANGÉ: profile_video au lieu de categorie_video
    statut_video: "brouillon",
    actif_video: 1
  });
  
  const [files, setFiles] = useState({
    video_file: null
  });
  
  const [errors, setErrors] = useState({});
  const [existingVideo, setExistingVideo] = useState("");

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setCurrentProfile(String(userData.profile || "").trim());
      } catch (error) {
        console.error("Error parsing user:", error);
        setServerError("Erreur de session utilisateur");
      }
    } else {
      setServerError("Session non trouvée. Veuillez vous reconnecter.");
      setTimeout(() => navigate("/login"), 2000);
      return;
    }
    
    fetchTutoDetails();
  }, [id, navigate]);

  const fetchTutoDetails = async () => {
    setLoading(true);
    setServerError("");
    
    try {
      const token = localStorage.getItem("access_token");
      const url = `${API_BASE_URL}/tuto/get.php?id=${id}`;
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      
      if (response.data?.success) {
        const tuto = response.data.data;
        
        setForm({
          titre_video: tuto.titre_video || "",
          description_video: tuto.description_video || "",
          url_video: tuto.url_video || "",
          profile_video: tuto.profile_video || "2", // CHANGÉ: profile_video
          statut_video: tuto.statut_video || "brouillon",
          actif_video: tuto.actif_video ? 1 : 0
        });
        
        setExistingVideo(tuto.video_url_complete || "");
        
        // Déterminer la source de la vidéo
        if (tuto.url_video) {
          setVideoSource('url');
        } else if (tuto.video_url_complete) {
          setVideoSource('file');
        }
        
      } else {
        setServerError(response.data?.message || "Impossible de charger le tutoriel.");
      }
    } catch (err) {
      handleFetchError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchError = (err) => {
    console.error("Fetch error:", err);
    if (err.response?.status === 401) {
      setServerError("Session expirée. Veuillez vous reconnecter.");
      setTimeout(() => navigate("/login"), 2000);
    } else if (err.response?.status === 403) {
      setServerError("Accès interdit. Admin/gestionnaire requis.");
    } else if (err.response?.status === 404) {
      setServerError("Tutoriel non trouvé.");
    } else if (err.response?.status === 500) {
      setServerError("Erreur serveur. Veuillez contacter l'administrateur.");
    } else if (err.request) {
      setServerError("Serveur inaccessible. Vérifiez votre connexion.");
    } else {
      setServerError("Erreur technique lors du chargement des données.");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileChange = (e) => {
    const { name } = e.target;
    const file = e.target.files[0];
    
    if (file) {
      setFiles(prev => ({ ...prev, [name]: file }));
      
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: "" }));
      }
    }
  };

  const validate = () => {
    const e = {};
    
    if (!form.titre_video.trim()) e.titre_video = "Le titre est obligatoire";
    if (!form.profile_video) e.profile_video = "Le profile destinataire est obligatoire"; // CHANGÉ
    
    const hasVideoFile = files.video_file && files.video_file.size > 0;
    const hasUrlVideo = form.url_video.trim() !== '';
    const hasExistingVideo = existingVideo && existingVideo.trim() !== '';

    // Validation uniquement si on est en mode fichier et qu'un nouveau fichier est sélectionné
    if (videoSource === 'file' && files.video_file) {
      const allowedTypes = ['video/mp4', 'video/webm', 'video/ogg'];
      const maxSize = 100 * 1024 * 1024; // 100MB (comme dans TutorialCreate)
      
      if (!allowedTypes.includes(files.video_file.type)) {
        e.video_file = "Format vidéo non autorisé. Formats acceptés: MP4, WebM, OGG";
      } else if (files.video_file.size > maxSize) {
        e.video_file = "Fichier trop volumineux (max 100MB)";
      }
    }
    
    // Validation pour le mode URL
    if (videoSource === 'url' && hasUrlVideo && !isValidUrl(form.url_video)) {
      e.url_video = "URL invalide";
    }
    
    // Validation générale : doit avoir une source vidéo (URL, fichier existant, ou nouveau fichier)
    if (!hasVideoFile && !hasUrlVideo && !hasExistingVideo) {
      e.video = "Vous devez fournir soit un fichier vidéo, soit une URL vidéo";
    }
    
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    setSuccessMsg("");
    setUploadProgress(0);
    
    if (!validate()) {
      return;
    }
    
    setSaving(true);

    try {
      const url = `${API_BASE_URL}/tuto/update.php?id=${id}`;
      const token = localStorage.getItem("access_token");

      if (!token) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setSaving(false);
        return;
      }

      const formData = new FormData();
      
      formData.append("titre_video", form.titre_video);
      formData.append("description_video", form.description_video);
      formData.append("url_video", videoSource === 'url' ? form.url_video : "");
      formData.append("profile_video", form.profile_video); // CHANGÉ
      formData.append("statut_video", form.statut_video);
      formData.append("actif_video", form.actif_video);
      
      if (files.video_file) {
        formData.append("video_file", files.video_file);
      }

      const response = await axios.post(url, formData, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        },
        timeout: 30000,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      const data = response.data;
      
      if (data.success) {
        const profileLabel = PROFILE_OPTIONS.find(p => p.value === form.profile_video)?.label || form.profile_video; // CHANGÉ
        const statutLabel = STATUT_OPTIONS.find(s => s.value === form.statut_video)?.label || form.statut_video;
        
        setSuccessMsg(`✅ Tutoriel mis à jour avec succès !\n\n• Titre : ${form.titre_video}\n• Profile destinataire : ${profileLabel}\n• Statut : ${statutLabel}\n• État : ${form.actif_video === 1 ? 'Actif' : 'Inactif'}`);
        
        // Mettre à jour les données existantes
        if (data.data && data.data.video_url_complete) {
          setExistingVideo(data.data.video_url_complete);
        }
        
        // Réinitialiser le fichier si uploadé
        if (files.video_file) {
          setFiles({ video_file: null });
        }
        
        setTimeout(() => {
          navigate(`/tutos/details/${id}`);
        }, 3000);
      } else {
        setServerError(data.message || "Erreur lors de la mise à jour.");
      } 
    } catch (err) {
      handleSubmitError(err);
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  const handleSubmitError = (err) => {
    if (err.response) {
      const { status, data } = err.response;
      
      if (status === 401) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
      } else if (status === 403) {
        setServerError(data?.message || "Permission refusée.");
      } else if (status === 400) {
        if (data.errors && Array.isArray(data.errors)) {
          const validationErrors = {};
          data.errors.forEach(error => {
            if (error.toLowerCase().includes("titre")) validationErrors.titre_video = error;
            else if (error.toLowerCase().includes("profile")) validationErrors.profile_video = error; // CHANGÉ
            else if (error.toLowerCase().includes("fichier vidéo")) validationErrors.video_file = error;
            else if (error.toLowerCase().includes("vidéo") || error.toLowerCase().includes("url")) {
              validationErrors.video = error;
            }
            else {
              setServerError(prev => prev ? `${prev} ${error}` : error);
            }
          });
          setErrors(validationErrors);
          setServerError("Veuillez corriger les erreurs dans le formulaire.");
        } else {
          setServerError(data?.message || `Erreur ${status}: Impossible de mettre à jour le tutoriel.`);
        }
      } else if (status === 413) {
        setServerError("Fichier trop volumineux. Maximum 100MB pour les vidéos."); // CHANGÉ
      } else if (status === 500) {
        setServerError("Erreur serveur. Veuillez contacter l'administrateur.");
      } else {
        setServerError(data?.message || `Erreur ${status}: Impossible de mettre à jour le tutoriel.`);
      }
    } else if (err.request) {
      setServerError("Serveur inaccessible. Vérifiez votre connexion et réessayez.");
    } else {
      setServerError("Erreur: " + err.message);
    }
    
    if (err.code === 'ECONNABORTED') {
      setServerError("Timeout: Le serveur met trop de temps à répondre. Réessayez.");
    }
  };

  const handleReset = () => {
    // Recharger les données originales
    fetchTutoDetails();
    setFiles({ video_file: null });
    setErrors({});
    setServerError("");
    setSuccessMsg("");
    setUploadProgress(0);
  };

  const handleGoBack = () => {
    navigate(`/tutos/details/${id}`);
  };

  const getProfileInfo = (profileValue) => {
    return PROFILE_OPTIONS.find(p => p.value === profileValue) || PROFILE_OPTIONS[0];
  };

  if (loading) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: '#0B3C5D', fontWeight: '600' }}>Modifier un tutoriel</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/dashboard" onClick={(e) => { e.preventDefault(); navigate("/dashboard"); }}>
                  Tableau de bord
                </a>
              </li>
              <li className="breadcrumb-item">
                <a href="/tutos/admin" onClick={(e) => { e.preventDefault(); navigate("/tutos/admin"); }}>
                  Tutoriels
                </a>
              </li>
              <li className="breadcrumb-item active" style={{ color: '#1E88C8' }}>Modifier</li>
            </ol>
          </nav>
        </div>

        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body">
              <div className="text-center py-5">
                <div className="spinner-border" role="status" style={{ color: '#0B3C5D' }}>
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-3" style={{ color: '#2E2E2E' }}>Chargement du tutoriel...</p>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (serverError && !loading && serverError.includes("Permission") || serverError.includes("interdit")) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: '#0B3C5D', fontWeight: '600' }}>Modifier un tutoriel</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/dashboard" onClick={(e) => { e.preventDefault(); navigate("/dashboard"); }}>
                  Tableau de bord
                </a>
              </li>
              <li className="breadcrumb-item">
                <a href="/tutos/admin" onClick={(e) => { e.preventDefault(); navigate("/tutos/admin"); }}>
                  Tutoriels
                </a>
              </li>
              <li className="breadcrumb-item active" style={{ color: '#1E88C8' }}>Modifier</li>
            </ol>
          </nav>
        </div>

        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body">
              <div className="text-center py-5">
                <div className="mb-4">
                  <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                    width: '80px',
                    height: '80px',
                    backgroundColor: '#FFEBEE',
                    color: '#f44336'
                  }}>
                    <i className="bi bi-exclamation-triangle fs-1"></i>
                  </div>
                </div>
                <h5 className="mb-3" style={{ color: '#0B3C5D', fontWeight: '600' }}>Accès restreint</h5>
                <p className="text-muted" style={{ color: '#2E2E2E', maxWidth: '500px', margin: '0 auto' }}>
                  {serverError}
                </p>
                
                <button
                  className="btn fw-semibold mt-4"
                  onClick={() => navigate("/tutos/admin")}
                  style={{
                    color: '#0B3C5D',
                    borderColor: '#0B3C5D',
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 24px'
                  }}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Retour aux tutoriels
                </button>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: '#0B3C5D', fontWeight: '600' }}>Modifier un tutoriel</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/dashboard" onClick={(e) => { e.preventDefault(); navigate("/dashboard"); }}>
                Tableau de bord
              </a>
            </li>
            <li className="breadcrumb-item">
              <a href="/tutos/admin" onClick={(e) => { e.preventDefault(); navigate("/tutos/admin"); }}>
                Tutoriels
              </a>
            </li>
            <li className="breadcrumb-item active" style={{ color: '#1E88C8' }}>Modifier</li>
            </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-body">
                <h5 className="card-title" style={{ 
                  color: '#0B3C5D',
                  borderBottom: '2px solid #F5F7FA',
                  paddingBottom: '16px',
                  marginBottom: '24px'
                }}>
                  <i className="bi bi-pencil-square me-2" style={{ color: '#1E88C8' }}></i>
                  Édition du tutoriel
                </h5>
                
                {serverError && (
                  <div className="alert alert-danger alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#ffebee',
                    borderColor: '#f44336',
                    color: '#c62828',
                    borderRadius: '8px',
                    borderLeft: '4px solid #f44336'
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      <div className="flex-grow-1">{serverError}</div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setServerError("")}
                      ></button>
                    </div>
                  </div>
                )}
                
                {successMsg && (
                  <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#e8f5e9',
                    borderColor: '#4caf50',
                    color: '#2e7d32',
                    borderRadius: '8px',
                    borderLeft: '4px solid #4caf50',
                    whiteSpace: "pre-line"
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-check-circle me-2" style={{ fontSize: '1.2rem' }}></i>
                      <div className="flex-grow-1">{successMsg}</div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setSuccessMsg("")}
                      ></button>
                    </div>
                  </div>
                )}

                <form className="row g-3" onSubmit={handleSubmit}>
                  {/* BLOC : Informations générales */}
                  <div className="col-12">
                    <div className="d-flex align-items-center mb-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                        width: '40px',
                        height: '40px',
                        backgroundColor: '#E3F2FD',
                        color: '#0B3C5D'
                      }}>
                        <i className="bi bi-info-circle"></i>
                      </div>
                      <h6 style={{ color: '#0B3C5D', fontWeight: '600', marginBottom: '0' }}>
                        Informations générales
                      </h6>
                    </div>
                  </div>

                  {/* Ligne 1 - Titre et Description */}
                  <div className="col-md-4">
                    <label htmlFor="titre_video" className="form-label" style={{ color: '#2E2E2E', fontWeight: '500' }}>
                      <i className="bi bi-film me-1" style={{ color: '#1E88C8' }}></i>
                      Titre du tutoriel <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" style={{ 
                        backgroundColor: '#F5F7FA',
                        borderColor: errors.titre_video ? '#f44336' : '#e0e0e0',
                        color: errors.titre_video ? '#f44336' : '#1E88C8'
                      }}>
                        <i className="bi bi-text-left"></i>
                      </span>
                      <input
                        type="text"
                        id="titre_video"
                        name="titre_video"
                        className={`form-control ${errors.titre_video ? "is-invalid" : ""}`}
                        value={form.titre_video}
                        onChange={handleChange}
                        disabled={saving}
                        placeholder="Ex: Introduction à la sécurité informatique"
                        autoComplete="off"
                        style={{ 
                          borderColor: errors.titre_video ? '#f44336' : '#e0e0e0',
                          borderRadius: '0 8px 8px 0',
                          color: '#2E2E2E'
                        }}
                      />
                    </div>
                    {errors.titre_video && <div className="invalid-feedback d-block mt-2" style={{ fontSize: '0.85rem' }}>{errors.titre_video}</div>}
                  </div>

                  <div className="col-md-8">
                    <label htmlFor="description_video" className="form-label" style={{ color: '#2E2E2E', fontWeight: '500' }}>
                      <i className="bi bi-card-text me-1" style={{ color: '#1E88C8' }}></i>
                      Description
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" style={{ 
                        backgroundColor: '#F5F7FA',
                        borderColor: '#e0e0e0',
                        color: '#1E88C8'
                      }}>
                        <i className="bi bi-text-paragraph"></i>
                      </span>
                      <input
                        type="text"
                        id="description_video"
                        name="description_video"
                        className="form-control"
                        value={form.description_video}
                        onChange={handleChange}
                        disabled={saving}
                        placeholder="Description"
                        autoComplete="off"
                        style={{ 
                          borderColor: '#e0e0e0',
                          borderRadius: '0 8px 8px 0',
                          color: '#2E2E2E'
                        }}
                      />
                    </div>
                  </div>

                  {/* Ligne 2 - Profile destinataire et Statut (CHANGÉ) */}
                  <div className="col-md-4">
                    <label htmlFor="profile_video" className="form-label" style={{ color: '#2E2E2E', fontWeight: '500' }}>
                      <i className="bi bi-person me-1" style={{ color: '#1E88C8' }}></i>
                      Profile destinataire <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" style={{ 
                        backgroundColor: '#F5F7FA',
                        borderColor: errors.profile_video ? '#f44336' : '#e0e0e0',
                        color: errors.profile_video ? '#f44336' : '#1E88C8'
                      }}>
                        <i className={`bi ${getProfileInfo(form.profile_video).icon}`}></i>
                      </span>
                      <select
                        id="profile_video"
                        name="profile_video"
                        className={`form-control ${errors.profile_video ? "is-invalid" : ""}`}
                        value={form.profile_video}
                        onChange={handleChange}
                        disabled={saving}
                        style={{ 
                          borderColor: errors.profile_video ? '#f44336' : '#e0e0e0',
                          borderRadius: '0 8px 8px 0',
                          color: '#2E2E2E'
                        }}
                      >
                        {PROFILE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {errors.profile_video && <div className="invalid-feedback d-block mt-2" style={{ fontSize: '0.85rem' }}>{errors.profile_video}</div>}
                    <small className="text-muted d-block mt-1" style={{ fontSize: '0.85rem' }}>
                      <i className="bi bi-info-circle me-1"></i>
                      {form.profile_video === '2' && "Visible par les contrôleurs et agents"}
                      {form.profile_video === '3' && "Visible par les gestionnaires, contrôleurs et agents"}
                      {form.profile_video === '4' && "Visible uniquement par les agents"}
                    </small>
                  </div>

                  <div className="col-md-4">
                    <label htmlFor="statut_video" className="form-label" style={{ color: '#2E2E2E', fontWeight: '500' }}>
                      <i className="bi bi-eye me-1" style={{ color: '#1E88C8' }}></i>
                      Statut
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" style={{ 
                        backgroundColor: '#F5F7FA',
                        borderColor: '#e0e0e0',
                        color: '#1E88C8'
                      }}>
                        <i className={`bi ${
                          form.statut_video === 'brouillon' ? 'bi-file-earmark' :
                          form.statut_video === 'publie' ? 'bi-eye' :
                          'bi-archive'
                        }`}></i>
                      </span>
                      <select
                        id="statut_video"
                        name="statut_video"
                        className="form-control"
                        value={form.statut_video}
                        onChange={handleChange}
                        disabled={saving}
                        style={{ 
                          borderColor: '#e0e0e0',
                          borderRadius: '0 8px 8px 0',
                          color: '#2E2E2E'
                        }}
                      >
                        {STATUT_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <small className="text-muted d-block mt-1" style={{ fontSize: '0.85rem' }}>
                      <i className={`bi me-1 ${
                        form.statut_video === 'brouillon' ? 'bi-file-earmark text-warning' :
                        form.statut_video === 'publie' ? 'bi-eye text-success' :
                        'bi-archive text-muted'
                      }`}></i>
                      {form.statut_video === 'brouillon' && "Brouillon"}
                      {form.statut_video === 'publie' && "Publié"}
                      {form.statut_video === 'archive' && "Archivé"}
                    </small>
                  </div>

                  {/* Ligne 3 - Activation */}
                  <div className="col-md-4">
                    <label className="form-label d-flex align-items-center mb-3" style={{ color: '#2E2E2E', fontWeight: '500' }}>
                      <i className="bi bi-toggle-on me-1" style={{ color: '#1E88C8' }}></i>
                      État d'activation
                    </label>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="actif_video"
                        name="actif_video"
                        checked={form.actif_video === 1}
                        onChange={(e) => {
                          setForm(prev => ({
                            ...prev,
                            actif_video: e.target.checked ? 1 : 0
                          }));
                        }}
                        disabled={saving}
                        style={{
                          width: '3em',
                          height: '1.5em',
                          cursor: saving ? 'not-allowed' : 'pointer'
                        }}
                      />
                      <label className="form-check-label ms-2" htmlFor="actif_video">
                        {form.actif_video === 1 ? (
                          <span className="text-success">
                            <i className="bi bi-check-circle me-1"></i>
                            Actif
                          </span>
                        ) : (
                          <span className="text-danger">
                            <i className="bi bi-x-circle me-1"></i>
                            Inactif
                          </span>
                        )}
                      </label>
                    </div>
                    <small className="text-muted d-block mt-1" style={{ fontSize: '0.85rem' }}>
                      {form.actif_video === 1 
                        ? "Le tutoriel est visible par les utilisateurs" 
                        : "Le tutoriel est masqué pour les utilisateurs"}
                    </small>
                  </div>

                  {/* BLOC : Contenu vidéo */}
                  <div className="col-12 mt-4">
                    <div className="d-flex align-items-center mb-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                        width: '40px',
                        height: '40px',
                        backgroundColor: '#E3F2FD',
                        color: '#0B3C5D'
                      }}>
                        <i className="bi bi-play-circle"></i>
                      </div>
                      <h6 style={{ color: '#0B3C5D', fontWeight: '600', marginBottom: '0' }}>
                        Contenu vidéo
                      </h6>
                    </div>
                    
                    {/* Erreur générale vidéo */}
                    {errors.video && (
                      <div className="col-12 mb-3">
                        <div className="alert alert-warning" style={{ 
                          backgroundColor: '#fff8e1',
                          borderColor: '#ff9800',
                          color: '#ff8f00',
                          borderRadius: '8px',
                          fontSize: '0.85rem'
                        }}>
                          <i className="bi bi-exclamation-triangle me-1"></i>
                          {errors.video}
                        </div>
                      </div>
                    )}
                    
                    {/* Sélection type de source */}
                    <div className="col-12 mb-4">
                      <label className="form-label d-flex align-items-center mb-3" style={{ color: '#2E2E2E', fontWeight: '500' }}>
                        <i className="bi bi-database me-2" style={{ color: '#1E88C8' }}></i>
                        Source de la vidéo
                      </label>
                      <div className="d-flex gap-3">
                        <div 
                          className="d-flex align-items-center p-3 rounded"
                          onClick={() => !saving && setVideoSource('url')}
                          style={{
                            flex: 1,
                            backgroundColor: videoSource === 'url' ? '#E3F2FD' : '#F5F7FA',
                            border: `2px solid ${videoSource === 'url' ? '#1E88C8' : '#e0e0e0'}`,
                            borderRadius: '8px',
                            cursor: saving ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            opacity: saving ? 0.6 : 1
                          }}
                        >
                          <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                            width: '40px',
                            height: '40px',
                            backgroundColor: videoSource === 'url' ? '#1E88C8' : '#B0BEC5',
                            color: 'white'
                          }}>
                            <i className="bi bi-link-45deg"></i>
                          </div>
                          <div>
                            <h6 style={{ color: '#0B3C5D', marginBottom: '4px' }}>URL externe</h6>
                            <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                              Utiliser un lien YouTube, Vimeo, etc.
                            </p>
                          </div>
                        </div>
                        
                        <div 
                          className="d-flex align-items-center p-3 rounded"
                          onClick={() => !saving && setVideoSource('file')}
                          style={{
                            flex: 1,
                            backgroundColor: videoSource === 'file' ? '#E3F2FD' : '#F5F7FA',
                            border: `2px solid ${videoSource === 'file' ? '#1E88C8' : '#e0e0e0'}`,
                            borderRadius: '8px',
                            cursor: saving ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            opacity: saving ? 0.6 : 1
                          }}
                        >
                          <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                            width: '40px',
                            height: '40px',
                            backgroundColor: videoSource === 'file' ? '#1E88C8' : '#B0BEC5',
                            color: 'white'
                          }}>
                            <i className="bi bi-upload"></i>
                          </div>
                          <div>
                            <h6 style={{ color: '#0B3C5D', marginBottom: '4px' }}>Upload fichier</h6>
                            <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                              {existingVideo ? "Fichier existant - télécharger un nouveau pour remplacer" : "Télécharger une vidéo"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* URL vidéo externe */}
                    {videoSource === 'url' && (
                      <div className="col-md-12">
                        <label htmlFor="url_video" className="form-label" style={{ color: '#2E2E2E', fontWeight: '500' }}>
                          <i className="bi bi-link me-1" style={{ color: '#1E88C8' }}></i>
                          URL vidéo
                        </label>
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: '#F5F7FA',
                            borderColor: errors.url_video ? '#f44336' : '#e0e0e0',
                            color: errors.url_video ? '#f44336' : '#1E88C8'
                          }}>
                            <i className="bi bi-globe"></i>
                          </span>
                          <input
                            type="url"
                            id="url_video"
                            name="url_video"
                            className={`form-control ${errors.url_video ? "is-invalid" : ""}`}
                            value={form.url_video}
                            onChange={handleChange}
                            disabled={saving}
                            placeholder="https://youtube.com/watch?v=... ou https://exemple.com/video.mp4"
                            autoComplete="off"
                            style={{ 
                              borderColor: errors.url_video ? '#f44336' : '#e0e0e0',
                              borderRadius: '0 8px 8px 0',
                              color: '#2E2E2E'
                            }}
                          />
                        </div>
                        {errors.url_video && <div className="invalid-feedback d-block mt-2" style={{ fontSize: '0.85rem' }}>{errors.url_video}</div>}
                        <small className="text-muted d-block mt-1" style={{ fontSize: '0.85rem' }}>
                          <i className="bi bi-info-circle me-1"></i>
                          Supporte YouTube, Vimeo, ou URLs directes vers fichiers vidéo
                        </small>
                      </div>
                    )}

                    {/* Fichier vidéo */}
                    {videoSource === 'file' && (
                      <div className="col-md-12">
                        <label htmlFor="video_file" className="form-label" style={{ color: '#2E2E2E', fontWeight: '500' }}>
                          <i className="bi bi-upload me-1" style={{ color: '#1E88C8' }}></i>
                          Fichier vidéo
                        </label>
                        
                        {existingVideo && !files.video_file && (
                          <div className="alert alert-info mb-3 p-3" style={{ 
                            backgroundColor: '#E3F2FD',
                            borderColor: '#4FC3F7',
                            borderRadius: '8px'
                          }}>
                            <div className="d-flex align-items-center">
                              <i className="bi bi-info-circle me-2" style={{ color: '#0B3C5D' }}></i>
                              <div>
                                <strong style={{ color: '#0B3C5D' }}>Fichier existant :</strong>
                                <p className="mb-0 mt-1" style={{ color: '#2E2E2E', fontSize: '0.9rem' }}>
                                  Un fichier vidéo existe déjà. Téléversez un nouveau fichier pour le remplacer.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className={`file-upload-area ${errors.video_file ? 'is-invalid' : ''}`} 
                          onClick={() => !saving && document.getElementById('video_file').click()}
                          style={{
                            border: `2px dashed ${errors.video_file ? '#f44336' : '#e0e0e0'}`,
                            borderRadius: '8px',
                            padding: '40px 20px',
                            textAlign: 'center',
                            backgroundColor: '#F5F7FA',
                            cursor: saving ? 'not-allowed' : 'pointer',
                            transition: 'all 0.3s ease',
                            position: 'relative'
                          }}
                          onDragOver={(e) => {
                            if (!saving) {
                              e.preventDefault();
                              e.currentTarget.style.backgroundColor = '#E3F2FD';
                              e.currentTarget.style.borderColor = '#1E88C8';
                            }
                          }}
                          onDragLeave={(e) => {
                            if (!saving) {
                              e.currentTarget.style.backgroundColor = '#F5F7FA';
                              e.currentTarget.style.borderColor = errors.video_file ? '#f44336' : '#e0e0e0';
                            }
                          }}
                        >
                          <input
                            type="file"
                            id="video_file"
                            name="video_file"
                            className="d-none"
                            onChange={handleFileChange}
                            disabled={saving}
                            accept="video/mp4,video/webm,video/ogg"
                          />
                          
                          {files.video_file ? (
                            <div>
                              <i className="bi bi-check-circle-fill fs-1 mb-2" style={{ color: '#4caf50' }}></i>
                              <h6 className="mb-1" style={{ color: '#4caf50' }}>Nouveau fichier sélectionné</h6>
                              <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                                {files.video_file.name} ({(files.video_file.size / (1024 * 1024)).toFixed(2)} MB)
                              </p>
                              <button
                                type="button"
                                className="btn btn-sm mt-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFiles(prev => ({ ...prev, video_file: null }));
                                }}
                                disabled={saving}
                                style={{
                                  color: '#f44336',
                                  borderColor: '#f44336',
                                  backgroundColor: 'transparent',
                                  fontSize: '0.8rem'
                                }}
                              >
                                <i className="bi bi-x-circle me-1"></i>
                                Supprimer
                              </button>
                            </div>
                          ) : existingVideo ? (
                            <div>
                              <i className="bi bi-file-earmark-play fs-1 mb-2" style={{ color: '#1E88C8' }}></i>
                              <h6 className="mb-1" style={{ color: '#0B3C5D' }}>Fichier existant détecté</h6>
                              <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                                Cliquez ou glissez-déposez pour remplacer la vidéo
                              </p>
                            </div>
                          ) : (
                            <div>
                              <i className="bi bi-cloud-upload fs-1 mb-2" style={{ color: '#1E88C8' }}></i>
                              <h6 className="mb-1" style={{ color: '#0B3C5D' }}>Cliquez ou glissez-déposez votre vidéo</h6>
                              <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                                Formats supportés : MP4, WebM, OGG (max 100MB)
                              </p>
                            </div>
                          )}
                        </div>
                        {errors.video_file && <div className="invalid-feedback d-block mt-2" style={{ fontSize: '0.85rem' }}>{errors.video_file}</div>}
                        
                        {/* Barre de progression */}
                        {uploadProgress > 0 && (
                          <div className="mt-3">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <small className="text-muted">Téléchargement en cours...</small>
                              <small className="text-muted fw-semibold">{uploadProgress}%</small>
                            </div>
                            <div className="progress" style={{ height: '6px', borderRadius: '3px' }}>
                              <div 
                                className="progress-bar" 
                                role="progressbar" 
                                style={{ 
                                  width: `${uploadProgress}%`,
                                  backgroundColor: '#4caf50',
                                  transition: 'width 0.3s ease'
                                }}
                              ></div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Boutons */}
                  <div className="col-12 d-flex justify-content-between mt-4 pt-4 border-top" style={{ borderColor: '#F5F7FA' }}>
                    <div className="d-flex gap-2">
                      <button 
                        type="submit" 
                        className="btn fw-semibold"
                        disabled={saving}
                        style={{
                          color: 'white',
                          backgroundColor: saving ? '#B0BEC5' : '#0B3C5D',
                          borderColor: saving ? '#B0BEC5' : '#0B3C5D',
                          borderRadius: '8px',
                          padding: '12px 32px',
                          fontSize: '1rem',
                          transition: 'all 0.3s ease',
                          opacity: saving ? 0.8 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (!saving) {
                            e.target.style.backgroundColor = '#1E88C8';
                            e.target.style.borderColor = '#1E88C8';
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 6px 12px rgba(11, 60, 93, 0.2)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!saving) {
                            e.target.style.backgroundColor = '#0B3C5D';
                            e.target.style.borderColor = '#0B3C5D';
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                          }
                        }}
                      >
                        {saving ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Mise à jour en cours...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-check-circle me-2"></i>
                            Enregistrer les modifications
                          </>
                        )}
                      </button>
                      
                      <button
                        type="button"
                        className="btn fw-semibold"
                        onClick={handleReset}
                        disabled={saving}
                        style={{
                          color: '#ff9800',
                          borderColor: '#ff9800',
                          backgroundColor: 'transparent',
                          borderRadius: '8px',
                          padding: '12px 24px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#FFF3E0';
                          e.target.style.borderColor = '#ff9800';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.borderColor = '#ff9800';
                        }}
                      >
                        <i className="bi bi-arrow-clockwise me-2"></i>
                        Réinitialiser
                      </button>
                    </div>
                    
                    <button
                      type="button"
                      className="btn fw-semibold"
                      onClick={handleGoBack}
                      disabled={saving}
                      style={{
                        color: '#2E2E2E',
                        borderColor: '#e0e0e0',
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        padding: '12px 24px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#F5F7FA';
                        e.target.style.borderColor = '#0B3C5D';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.borderColor = '#e0e0e0';
                      }}
                    >
                      <i className="bi bi-x-circle me-2"></i>
                      Annuler
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .form-label,
        .alert strong {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .form-control,
        .form-select,
        .form-text,
        .alert,
        .table,
        small {
          font-family: 'Roboto', sans-serif;
        }
        
        .form-control:focus,
        .form-select:focus {
          border-color: #1E88C8;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(11, 60, 93, 0.25);
        }
        
        .btn,
        .form-select,
        .input-group-text,
        .form-control {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:disabled,
        .form-select:disabled {
          background-color: '#F5F7FA';
          cursor: not-allowed;
        }
        
        .file-upload-area:hover {
          border-color: #1E88C8 !important;
          background-color: #E3F2FD !important;
        }
        
        .file-upload-area.is-invalid {
          background-color: #ffebee !important;
        }
        
        .form-check-input:checked {
          background-color: #0B3C5D;
          border-color: #0B3C5D;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .col-12.d-flex.justify-content-between {
            flex-direction: column;
            gap: 12px;
          }
          
          .col-12.d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .col-md-8, .col-md-4 {
            flex: 0 0 100%;
            max-width: 100%;
          }
        }
      `}</style>
    </Layout>
  );
};

export default TutorialEdit;